/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Encloses all types used in dedicated delivery flow.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */

import { DateObject } from "react-multi-date-picker";
import { DdRouteDashboardResponse } from "../../app/models/responseModels/DdRouteResponse";
import { FdpResponse } from "../../app/models/responseModels/FdpResponse";
import {
  FilterableColumns,
  FilterableColumnsState,
} from "../../components/common/filter/GridDataFilter";

export enum DD_COLUMN_NAME {
  CLIENTID = "clientId",
  CLIENTNAME = "clientName",
  TOTALSTORES = "totalStores",
  TOTALROUTES = "totalRoutes",
  PACKAGESTENDERED = "packagesTendered",
  ORDERSDELIVERED = "ordersDelivered",
  UNDELIVEREDNOATTEMPT = "undeliveredNoAttempt",
  UNDELIVEREDATTEMPT = "undeliveredAttempt",
  GROSSPERFORMANCE = "grossPerformance",
  NETPERFORMANCE = "netPerformance",
  CLIENTCREATEDON = "clientCreatedOn",
}

export enum DD_LABEL_NAME {
  CLIENTID = "Client ID",
  CLIENTNAME = "Client Name",
  TOTALSTORES = "Total Store(s)",
  TOTALROUTES = "Total Route(s)",
  PACKAGESTENDERED = "Package(s) Tendered",
  ORDERSDELIVERED = "Package(s) Delivered",
  UNDELIVEREDNOATTEMPT = "Controllable Misses",
  UNDELIVEREDATTEMPT = "Uncontrollable Misses",
  GROSSPERFORMANCE = "Gross Performance (In %)",
  NETPERFORMANCE = "Net Performance (In %)",
  CLIENTCREATEDON = "Client Created On",
}

export interface ActualHeader {
  Date: string;
  Store: string;
  "Trip ID": string;
  drvr_user_id: string;
  "Driver Request": string;
  "Enroute to Pickup": string;
  "On Time Enroute to Store": string;
  "At Pickup": string;
  "On Time Arrival": string;
  "Associate Dispense": string;
  "Load Start": string;
  "Driver Dwell Time (min)": number;
  "Load End": string;
  "Driver Load Time (min)": number;
  Departure: string;
  "On Time Departure": string;
  "Driver Last Delivery": string;
  "Trip Actual Time (min)": number;
  "Driver Doorstep Time (min)": number;
  "Enroute to Return": string;
  "At Return": string;
  "Trip Marked Complete": string;
  "Trip Planned Start": string;
  "Orders - Total": number;
  "Orders - Delivered": number;
  "Orders - Undelivered": number;
  "Orders - Undelivered - No Attempt": number;
  "Orders - Undelivered - Attempted": number;
  Miles: number;
  "Units - Total": number;
  "Units - Orders With Units": number;
}

export const ActualHeaderAsArr = [
  "Date",
  "Store",
  "Trip ID",
  "drvr_user_id",
  "Driver Request",
  "Enroute to Pickup",
  "On Time Enroute to Store",
  "At Pickup",
  "On Time Arrival",
  "Associate Dispense",
  "Load Start",
  "Driver Dwell Time (min)",
  "Load End",
  "Driver Load Time (min)",
  "Departure",
  "On Time Departure",
  "Driver Last Delivery",
  "Trip Actual Time (min)",
  "Driver Doorstep Time (min)",
  "Enroute to Return",
  "At Return",
  "Trip Marked Complete",
  "Trip Planned Start",
  "Orders - Total",
  "Orders - Delivered",
  "Orders - Undelivered",
  "Orders - Undelivered - No Attempt",
  "Orders - Undelivered - Attempted",
  "Miles",
  "Units - Total",
  "Units - Orders With Units",
];

export enum DD_STORE_COLUMN_NAME {
  STOREID = "storeId",
  CLIENTSTOREID = "clientStoreId",
  CLIENTID = "clientId",
  CLIENTNAME = "clientName",
  TIMEZONE = "timezone",
  STORECREATEDON = "storeCreatedOn",
  STOREUPDATEDON = "storeUpdatedOn",
  STORECREATEDBY = "storeCreatedBy",
  LOCATION = "location",
  LONGITUDE = "longitude",
  LATITUDE = "latitude",
  FDPS = "fdps",
  GROSSPERFORMANCE = "grossPerformance",
  NETPERFORMANCE = "netPerformance",
  FIRSTSERVICE = "firstManifestIngestion",
  LATESTMANIFESTINGESTION = "latestManifestIngestion"
}

export enum DD_STORE_LABEL_NAME {
  STOREID = "Store ID",
  CLIENTSTOREID = "Client Store ID",
  CLIENTID = "Client ID",
  CLIENTNAME = "Client Name",
  TIMEZONE = "Timezone",
  STORECREATEDON = "Created On",
  STOREUPDATEDON = "Updated On",
  STORECREATEDBY = "Created By",
  FDPS = "Associated FDP",
  LOCATION = "Location",
  LONGITUDE = "longitude",
  LATITUDE = "latitude",
  GROSSPERFORMANCE = "Gross Performance(In %)",
  NETPERFORMANCE = "Net Performance(In %)",
  FIRSTSERVICE = "First Service",
  LATESTMANIFESTINGESTION = "Latest Manifest"
}

export type ClientStoreProps = {
  clientId: number;
  clientName: string;
  startDate: Date;
  endDate: Date;
};

export type RtsProps = {
  clientId: number;
  clientName: string;
  startDate: Date | string;
  endDate: Date | string;
};

export type StoreRouteProps = {
  clientId: number;
  storeId: number;
  startDate: DateObject;
  endDate: DateObject;
  close?: any;
};

export interface RouteDataArgs {
  clientId: number;
  pageIndex: number;
  pageSize: number;
  startDate: string;
  endDate: string;
  filters?: FilterableColumnsState;
}

export interface RtsDataArgs {
  clientId: number;
  pageIndex: number;
  pageSize: number;
  startDate: string;
  endDate: string;
  filters: FilterableColumnsState;
}

export interface RouteMetricsArgs {
  route: DdRouteDashboardResponse;
  close: any;
}

export interface RoutePerStoreDataArgs {
  storeId: number;
  clientId: number;
  pageIndex: number;
  pageSize: number;
  startDate: string;
  endDate: string;
  filter: FilterableColumnsState;
}

export type ClientGeneralProps = {
  clientId: number;
};

export enum DD_RTS_COLUMN_NAME {
  ID = "id",
  USERID = "userId",
  USERNAME = "userName",
  STOREID = "storeId",
  STORENAME = "storeName",
  DATE = "operationDate",
  EVENTDESCRIPTION = "eventDescription",
  OTHER_REASON = "otherReason",
  BARCODE = "barcode",
  UUID = "uuid",
  IMAGEURL = "imageUrl",
  RTSLATITUDE = "rtsLatitude",
  RTSLONGITUDE = "rtsLongitude",
  EXCEPTIONLATITUDE = "exceptionLatitude",
  EXCEPTIONLONGITUDE = "exceptionLongitude",
  RTSSCANNEDTIMESTAMP = "rtsScanTimeInGmt",
  RTSSCANNEDLOCALTIMESTAMP = "rtsScanLocalTime",
  EXCEPTIONSCANNEDTIMESTAMP = "exceptionScanTimeInGmt",
  EXCEPTIONSCANNEDLOCALTIMESTAMP = "exceptionScanLocalTime",
  SCANTYPE = "scanType",
  ACTION = "action",
  ISRTSSUBMITTED = "isRtsSubmitted",
  CREATEDBY = "createdBy",
  CREATEDON = "createdOn",
  UPDATEDBY = "updatedBy",
  UPDATEDON = "updatedOn",
  ROUTEID = "tripId",
  SUBREASONCODE = "subReasonCode",
  REASON = "reason"
}

export enum DD_RTS_LABEL_NAME {
  ID = "ID",
  USERID = "DRIVER ID",
  USERNAME = "DRIVER NAME",
  STOREID = "STORE ID",
  STORENAME = "STORE NAME",
  DATE = "DATE",
  REASON = "REASON",
  BARCODE = "BARCODE",
  UUID = "UUID",
  IMAGEURL = "IMAGE",
  RTSLATITUDE = "RTS LATITUDE",
  RTSLONGITUDE = "RTS LONGITUDE",
  EXCEPTIONLATITUDE = "EXCEPTION LATITUDE",
  EXCEPTIONLONGITUDE = "EXCEPTION LONGITUDE",
  RTSSCANNEDTIMESTAMP = "RTS SCANNED TIMESTAMP(UTC)",
  RTSSCANNEDLOCALTIMESTAMP = "RTS SCANNED LOCALTIME",
  EXCEPTIONSCANNEDTIMESTAMP = "EXCEPTION SCANNED TIMESTAMP(UTC)",
  EXCEPTIONSCANNEDLOCALTIMESTAMP = "EXCEPTION SCANNED LOCALTIME",
  SCANTYPE = "SCANTYPE",
  ACTION = "ACTION",
  ISRTSSUBMITTED = "RTS",
  CREATEDBY = "CREATED BY",
  CREATEDON = "CREATED ON",
  UPDATEDBY = "UPDATED BY",
  UPDATEDON = "UPDATED ON",
  ROUTEID = "ROUTE ID",
  SUBREASONCODE = "SUB REASON"
}

export enum DD_ROUTE_COLUMN_NAME {
  ID = "id",
  DATE = "date",
  STOREID = "storeId",
  TRIPID = "tripId",
  DRIVERNAME = "driverName",
  DRIVERID = "userId",
  DRIVERREQUEST = "driverRequest",
  ENROUTETOPICKUP = "enrouteToPickUp",
  ONROUTEENROUTETOPICKUP = "onRouteEnrouteToPickUp",
  ATPICKUP = "atPickUp",
  ONTIMEARRIVAL = "onTimeArrival",
  ASSOCIATEDISPENSE = "associateDispense",
  LOADSTART = "loadStart",
  DRIVERDWELLTIME = "driverDwellTime",
  LOADEND = "loadEnd",
  DRIVERLOADTIME = "driverLoadTime",
  DEPARTURE = "departure",
  ONTIMEDEPARTURE = "onTimeDeparture",
  DRIVERLASTDELIVERY = "driverLastDelivery",
  TRIPACTUALTIME = "tripActualTime",
  DRIVERDOORSTEPTIME = "driverDoorstepTime",
  ENROUTETORETURN = "enrouteToReturn",
  ATRETURN = "atReturn",
  TRIPMAKEDCOMPLETE = "tripMakedComplete",
  TRIPPLANNED = "tripPlanned",
  TOTALORDERS = "totalOrders",
  ORDERSDELIVERED = "ordersDelivered",
  ORDERSUNDELIVERED = "ordersUndelivered",
  ORDERSUNDELIVEREDNOATTEMPT = "ordersUndeliveredNoAttempt",
  ORDERSUNDELIVEREDATTEMPT = "ordersUndeliveredAttempt",
  MILES = "miles",
  UNITSTOTAL = "unitsTotal",
  UNITSORDERSWITHUNITS = "unitsOrdersWithUnits",
  UPDATEDON = "updatedOn",
  CREATEDON = "createdOn",
  CREATEDBY = "createdBy",
  UPDATEDBY = "updatedBy",
}

export enum DD_ROUTE_LABEL_NAME {
  ID = "Id",
  DATE = "Date",
  STOREID = "Client Store ID",
  TRIPID = "Trip ID",
  DRIVERNAME = "Driver Name",
  DRIVERID = "Driver ID",
  DRIVERREQUEST = "Driver Request",
  ENROUTETOPICKUP = "Enroute To PickUp",
  ONROUTEENROUTETOPICKUP = "On Time Enroute to Store",
  ATPICKUP = "At Pickup",
  ONTIMEARRIVAL = "On Time Arrival",
  ASSOCIATEDISPENSE = "Associate Dispense",
  LOADSTART = "Load Start",
  DRIVERDWELLTIME = "Driver Dwell Time (min)",
  LOADEND = "Load End",
  DRIVERLOADTIME = "Driver Load Time (min)",
  DEPARTURE = "Departure",
  ONTIMEDEPARTURE = "On Time Departure",
  DRIVERLASTDELIVERY = "Driver Last Delivery",
  TRIPACTUALTIME = "Trip Actual Time (min)",
  DRIVERDOORSTEPTIME = "Driver Doorstep Time (min)",
  ENROUTETORETURN = "Enroute to Return",
  ATRETURN = "At Return",
  TRIPMAKEDCOMPLETE = "Trip Marked Complete",
  TRIPPLANNED = "Trip Planned Start",
  TOTALORDERS = "Orders - Total",
  ORDERSDELIVERED = "Orders - Delivered",
  ORDERSUNDELIVERED = "Orders - Undelivered",
  ORDERSUNDELIVEREDNOATTEMPT = "Orders - Undelivered - No Attempt",
  ORDERSUNDELIVEREDATTEMPT = "Orders - Undelivered - Attempted",
  MILES = "Miles",
  UNITSTOTAL = "Units - Total",
  UNITSORDERSWITHUNITS = "Units - Orders With Units",
  UPDATEDON = "Updated On",
  CREATEDON = "Created On",
  CREATEDBY = "Created By",
  UPDATEDBY = "Updated By",
}

export enum DD_ROUTE_PER_STORE_COLUMN_NAME {
  DATE = "date",
  FDCSTOREID = "storeId",
  CLIENTSTOREID = "clientStoreId",
  TRIPID = "tripId",
  DRIVERNAME = "driverName",
  DRIVERID = "userId",
  TRIPACTUALTIME = "tripActualTime",
  TOTALORDERS = "totalOrders",
  ORDERSDELIVERED = "ordersDelivered",
  ORDERSUNDELIVEREDNOATTEMPT = "ordersUndeliveredNoAttempt",
  ORDERSUNDELIVEREDATTEMPT = "ordersUndeliveredAttempt",
  MILES = "miles",
  TOTALRTSPACKAGES = "totalRtsCount",
}

export enum DD_ROUTE_PER_STORE_LABEL_NAME {
  DATE = "Date",
  FDCSTOREID = "Store ID",
  CLIENTSTOREID = "Client Store ID",
  TRIPID = "Route ID",
  DRIVERNAME = "Driver Name",
  DRIVERID = "Driver ID",
  TRIPACTUALTIME = "Trip Actual Time (mins)",
  TOTALORDERS = "Package(s) Tendered",
  ORDERSDELIVERED = "Package(s) Delivered",
  ORDERSUNDELIVEREDNOATTEMPT = "Controllable Misses",
  ORDERSUNDELIVEREDATTEMPT = "Uncontrollable Misses",
  MILES = "Mileage",
  TOTALRTSPACKAGES = "Total RTS Package(s)",
}

export const IMPORT_FILE_HEADERS = [
  "Date",
  "Store",
  "Trip ID",
  "Van ID",
  "Driver Request",
  "Enroute to Pickup",
  "On Time Enroute to Store",
  "At Pickup",
  "On Time Arrival",
  "Associate Dispense",
  "Load Start",
  "Driver Dwell Time",
  "Load End",
  "Driver Load Time (min)",
  "Departure",
  "On Time Departure",
  "Driver Last Delivery",
  "Trip Actual Time (min)",
  "Driver Doorstep Time (min)",
  "Enroute to Return",
  "At Return",
  "Trip Marked Complete",
  "Trip Planned Start",
  "Orders - Total",
  "Orders - Delivered",
  "Orders - Undelivered",
  "Orders - Undelivered - No Attempt",
  "Orders - Undelivered - Attempted",
  "Miles",
  "Units - Total",
  "Units - Orders with Units",
];

export interface RouteExportDataArgs {
  clientId: number;
  routeIds: number[] | undefined;
  startDate: string;
  endDate: string;
  filters?: FilterableColumnsState;
}

export interface LangingPageExportDataArgs {
  startDate: string;
  endDate: string;
}

// argument type required for server call for data on store page
export interface StoreDataArgs {
  clientId: number;
  storeIds: string;
  startDate: string;
  endDate: string;
}

export interface StoreAssociatedRoutesDataArgs {
  clientId: number;
  storeId: number;
  routeIds: number[];
  startDate: string;
  endDate: string;
  filters: FilterableColumnsState;
}

export interface RtsEventType {
  id: number;
  eventTypeId: number;
  eventName: string;
  eventDescription: string;
  reasonCode?: string;
}
export const ddStoreGridFilter: FilterableColumns = {
  storeId: {
    displayName: "Store ID",
    options: [],
    type: "string",
  },
  clientStoreId: {
    displayName: "Client Store ID",
    options: [],
    type: "number",
  },
  storeCreatedOn: {
    displayName: "Created On",
    options: [],
    type: "datetime-range",
  },
  storeUpdatedOn: {
    displayName: "Updated On",
    options: [],
    type: "datetime-range",
  },
  storeCreatedBy: {
    displayName: "Created By",
    options: [],
    type: "string",
  },
  location: {
    displayName: "Location",
    options: [],
    type: "string",
  },
  fdps: {
    displayName: "FDP",
    options: [],
    type: "string",
  },
  grossPerformance: {
    displayName: "Gross Performance",
    options: [],
    type: "number",
  },
  netPerformance: {
    displayName: "Net Performance",
    options: [],
    type: "number",
  },
};

export const ddClientGridFilter: FilterableColumns = {
  clientId: {
    displayName: "Client ID",
    options: [],
    type: "number",
  },
  clientName: {
    displayName: "Client Name",
    options: [],
    type: "string",
  },
  totalStores: {
    displayName: "Total Store(s)",
    options: [],
    type: "number",
  },
  totalRoutes: {
    displayName: "Total Route(s)",
    options: [],
    type: "number",
  },
  packagesTendered: {
    displayName: "Package(s) Tendered",
    options: [],
    type: "number",
  },
  ordersDelivered: {
    displayName: "Package(s) Delivered",
    options: [],
    type: "number",
  },
  undeliveredNoAttempt: {
    displayName: "Controllable Misses",
    options: [],
    type: "number",
  },
  undeliveredAttempt: {
    displayName: "Uncontrollable Misses",
    options: [],
    type: "number",
  },
  grossPerformance: {
    displayName: "Gross Performance",
    options: [],
    type: "number",
  },
  netPerformance: {
    displayName: "Net Performance",
    options: [],
    type: "number",
  },
  clientCreatedOn: {
    displayName: "Client Created On",
    options: [],
    type: "datetime-range",
  },
};

export interface MultiRtsData {
  type: string;
  barcode?: string;
  image?: string;
  reasonId: number;
}

export const ddRouteGridFilter: FilterableColumns = {
  id: {
    displayName: "Id",
    options: [],
    type: "number",
  },
  date: {
    displayName: "Date",
    options: [],
    type: "date-range",
  },
  clientStoreId: {
    displayName: "Client Store ID",
    options: [],
    type: "string",
  },
  tripId: {
    displayName: "Trip ID",
    options: [],
    type: "string",
  },
  driverName: { displayName: "Driver Name", options: [], type: "string" },
  userId: { displayName: "Driver ID", options: [], type: "number" },
  driverRequest: {
    displayName: "Driver Request",
    options: [],
    type: "datetime-range",
  },
  enrouteToPickUp: {
    displayName: "Enroute To Pickup",
    options: [],
    type: "datetime-range",
  },
  onRouteEnrouteToPickUp: {
    displayName: "On Route Enroute To Store",
    options: [],
    type: "boolean",
  },
  atPickUp: { displayName: "At Pickup", options: [], type: "datetime-range" },
  onTimeArrival: {
    displayName: "On Time Arrival",
    options: [],
    type: "boolean",
  },
  associateDispense: {
    displayName: "Associate Dispense",
    options: [],
    type: "datetime-range",
  },
  loadStart: { displayName: "Load Start", options: [], type: "datetime-range" },
  driverDwellTime: {
    displayName: "Driver Dwell Time",
    options: [],
    type: "number",
  },
  loadEnd: { displayName: "Load End", options: [], type: "datetime-range" },
  driverLoadTime: {
    displayName: "Driver Load Time",
    options: [],
    type: "number",
  },
  departure: { displayName: "Departure", options: [], type: "datetime-range" },
  onTimeDeparture: {
    displayName: "On Time Departure",
    options: [],
    type: "boolean",
  },
  driverLastDelivery: {
    displayName: "Driver Last Delivery",
    options: [],
    type: "datetime-range",
  },
  tripActualTime: {
    displayName: "Trip Actual Time",
    options: [],
    type: "number",
  },
  driverDoorstepTime: {
    displayName: "Driver Doorstep Time",
    options: [],
    type: "number",
  },
  enrouteToReturn: {
    displayName: "Enroute To Return",
    options: [],
    type: "datetime-range",
  },
  atReturn: { displayName: "At Return", options: [], type: "datetime-range" },
  tripMakedComplete: {
    displayName: "Trip Maked Complete",
    options: [],
    type: "datetime-range",
  },
  tripPlanned: {
    displayName: "Trip Planned",
    options: [],
    type: "datetime-range",
  },
  totalOrders: { displayName: "Total Orders", options: [], type: "number" },
  ordersDelivered: {
    displayName: "Orders Delivered",
    options: [],
    type: "number",
  },
  ordersUndelivered: {
    displayName: "Orders Undelivered",
    options: [],
    type: "number",
  },
  ordersUndeliveredNoAttempt: {
    displayName: "Orders Undelivered No Attempt",
    options: [],
    type: "number",
  },
  ordersUndeliveredAttempt: {
    displayName: "Orders Undelivered Attempt",
    options: [],
    type: "number",
  },
  miles: { displayName: "Miles", options: [], type: "number" },
  unitsTotal: { displayName: "Units Total", options: [], type: "number" },
  unitsOrdersWithUnits: {
    displayName: "Units Orders With Units",
    options: [],
    type: "number",
  },
  updatedOn: { displayName: "Updated On", options: [], type: "datetime-range" },
  createdOn: { displayName: "Created On", options: [], type: "datetime-range" },
  createdBy: { displayName: "Created By", options: [], type: "string" },
  updatedBy: { displayName: "Updated By", options: [], type: "string" },
};

export const ddRoutePerStoreGridFilter: FilterableColumns = {
  date: {
    displayName: "Date",
    options: [],
    type: "date-range",
  },
  clientStoreId: {
    displayName: "Client Store ID",
    options: [],
    type: "string",
  },
  storeId: {
    displayName: "Store ID",
    options: [],
    type: "string",
  },
  tripId: {
    displayName: "Route ID",
    options: [],
    type: "string",
  },
  driverName: { displayName: "Driver Name", options: [], type: "number" },
  userId: { displayName: "Driver ID", options: [], type: "number" },
  totalOrders: {
    displayName: "Package(s) Tendered",
    options: [],
    type: "number",
  },
  ordersDelivered: {
    displayName: "Package(s) Delivered",
    options: [],
    type: "number",
  },
  ordersUndeliveredNoAttempt: {
    displayName: "Controllable Misses",
    options: [],
    type: "number",
  },
  ordersUndeliveredAttempt: {
    displayName: "Uncontrollable Misses",
    options: [],
    type: "number",
  },
  miles: { displayName: "Mileage", options: [], type: "number" },
  tripActualTime: {
    displayName: "Trip Actual Time (Mins)",
    options: [],
    type: "number",
  },
};

export const ddRtsGridFilter: FilterableColumns = {
  id: {
    displayName: "Id",
    options: [],
    type: "number",
  },
  tripId: {
    displayName: "Route ID",
    options: [],
    type: "string",
  },
  operationDate: {
    displayName: "Date",
    options: [],
    type: "datetime-range",
  },
  userId: {
    displayName: "Driver Id",
    options: [],
    type: "number",
  },
  userName: {
    displayName: "Driver Name",
    options: [],
    type: "string",
  },
  storeName: {
    displayName: "Store Name",
    options: [],
    type: "string",
  },
  barcode: {
    displayName: "Barcode",
    options: [],
    type: "string",
  },
  scanTimeInGmt: {
    displayName: "Scan Time (UTC)",
    options: [],
    type: "datetime-range-utc",
  },
  scanLocalTime: {
    displayName: "Scan Local Time",
    options: [],
    type: "datetime-range",
  },
  scanType: {
    displayName: "Scan Type",
    options: [],
    type: "string",
  },
  action: {
    displayName: "Action",
    options: [],
    type: "string",
  },
  createdBy: {
    displayName: "Created By",
    options: [],
    type: "string",
  },
  createdOn: {
    displayName: "Created On",
    options: [],
    type: "datetime-range",
  },
  updatedBy: {
    displayName: "Updated By",
    options: [],
    type: "string",
  },
  updatedOn: {
    displayName: "Updated On",
    options: [],
    type: "datetime-range",
  },
};
